import React from 'react';
import dateUtils from '../../../../../../../utils/date-format';

export interface InstallmentPlanSummary {
  planId: number;
  type: 'credit_card' | 'bank_transfer';
  totalAmount: number;
  allocationAmount: number;
  numberOfInstallments: number;
  paidCount: number;
  pendingCount: number;
  failedCount: number;
  nextPaymentDate: Date | null;
  createdAt: Date;
}

export interface InstallmentSummaryProps {
  installmentSummaries: InstallmentPlanSummary[];
  totalInstallmentAmount: number;
}

export const InstallmentSummary: React.FC<InstallmentSummaryProps> = ({
  installmentSummaries,
  totalInstallmentAmount,
}) => {
  if (installmentSummaries.length === 0) return null;

  const creditCardPlans = installmentSummaries.filter(
    (p) => p.type === 'credit_card',
  );
  const bankTransferPlans = installmentSummaries.filter(
    (p) => p.type === 'bank_transfer',
  );

  return (
    <div
      style={{
        marginBottom: '16px',
        padding: '16px',
        backgroundColor: '#f0f7ff',
        borderRadius: '8px',
        border: '1px solid #90CAF9',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
        }}
      >
        <i
          className="pi pi-calendar"
          style={{ color: '#1976D2', fontSize: '20px' }}
        ></i>
        <span
          style={{ fontWeight: 'bold', color: '#1976D2', fontSize: '16px' }}
        >
          הוראות קבע משויכות ({installmentSummaries.length})
        </span>
        <span
          style={{
            marginRight: 'auto',
            fontSize: '14px',
            color: '#424242',
            fontWeight: 'bold',
          }}
        >
          סה"כ: ₪{totalInstallmentAmount.toLocaleString()}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
        {creditCardPlans.length > 0 && (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '8px',
              }}
            >
              <i
                className="pi pi-credit-card"
                style={{ color: '#E91E63', fontSize: '16px' }}
              ></i>
              <span style={{ fontWeight: 600, color: '#E91E63' }}>
                הוראות קבע באשראי ({creditCardPlans.length})
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {creditCardPlans.map((plan) => (
                <div
                  key={`cc-${plan.planId}`}
                  style={{
                    backgroundColor: 'white',
                    padding: '10px 14px',
                    borderRadius: '6px',
                    border: '1px solid #F48FB1',
                    flex: '1 1 280px',
                    minWidth: '280px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '6px',
                    }}
                  >
                    <span style={{ color: '#666', fontSize: '13px' }}>
                      סכום לשירות זה:
                    </span>
                    <span style={{ fontWeight: 'bold', color: '#E91E63' }}>
                      ₪{plan.allocationAmount.toLocaleString()}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '6px',
                    }}
                  >
                    <span style={{ color: '#666', fontSize: '13px' }}>
                      מספר תשלומים:
                    </span>
                    <span style={{ fontWeight: 'bold' }}>
                      {plan.numberOfInstallments}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '6px',
                    }}
                  >
                    <span style={{ color: '#666', fontSize: '13px' }}>
                      סטטוס:
                    </span>
                    <span>
                      <span style={{ color: '#22c55e', fontWeight: 'bold' }}>
                        {plan.paidCount} שולמו
                      </span>
                      {' | '}
                      <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                        {plan.pendingCount} ממתינים
                      </span>
                      {plan.failedCount > 0 && (
                        <>
                          {' | '}
                          <span
                            style={{ color: '#ef4444', fontWeight: 'bold' }}
                          >
                            {plan.failedCount} נכשלו
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                  {plan.nextPaymentDate && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingTop: '6px',
                        borderTop: '1px solid #eee',
                      }}
                    >
                      <span style={{ color: '#666', fontSize: '13px' }}>
                        תשלום הבא:
                      </span>
                      <span style={{ fontWeight: 'bold', color: '#1976D2' }}>
                        {dateUtils.displayFormatDate(plan.nextPaymentDate)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {bankTransferPlans.length > 0 && (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '8px',
              }}
            >
              <i
                className="pi pi-building"
                style={{ color: '#3F51B5', fontSize: '16px' }}
              ></i>
              <span style={{ fontWeight: 600, color: '#3F51B5' }}>
                הוראות קבע בנקאיות ({bankTransferPlans.length})
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {bankTransferPlans.map((plan) => (
                <div
                  key={`bt-${plan.planId}`}
                  style={{
                    backgroundColor: 'white',
                    padding: '10px 14px',
                    borderRadius: '6px',
                    border: '1px solid #9FA8DA',
                    flex: '1 1 280px',
                    minWidth: '280px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '6px',
                    }}
                  >
                    <span style={{ color: '#666', fontSize: '13px' }}>
                      סכום לשירות זה:
                    </span>
                    <span style={{ fontWeight: 'bold', color: '#3F51B5' }}>
                      ₪{plan.allocationAmount.toLocaleString()}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '6px',
                    }}
                  >
                    <span style={{ color: '#666', fontSize: '13px' }}>
                      מספר תשלומים:
                    </span>
                    <span style={{ fontWeight: 'bold' }}>
                      {plan.numberOfInstallments}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '6px',
                    }}
                  >
                    <span style={{ color: '#666', fontSize: '13px' }}>
                      סטטוס:
                    </span>
                    <span>
                      <span style={{ color: '#22c55e', fontWeight: 'bold' }}>
                        {plan.paidCount} שולמו
                      </span>
                      {' | '}
                      <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                        {plan.pendingCount} ממתינים
                      </span>
                      {plan.failedCount > 0 && (
                        <>
                          {' | '}
                          <span
                            style={{ color: '#ef4444', fontWeight: 'bold' }}
                          >
                            {plan.failedCount} נכשלו
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                  {plan.nextPaymentDate && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingTop: '6px',
                        borderTop: '1px solid #eee',
                      }}
                    >
                      <span style={{ color: '#666', fontSize: '13px' }}>
                        תשלום הבא:
                      </span>
                      <span style={{ fontWeight: 'bold', color: '#1976D2' }}>
                        {dateUtils.displayFormatDate(plan.nextPaymentDate)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
